import { z } from 'zod';
import { AllowedWebsiteSchemaIPG } from '../../../backend_config.types';
import {
    CloverHeartbeatOutcome,
    type CloverHeartbeatMerchantDetails,
    type CloverHeartbeatNormalizedResult,
    type CloverHeartbeatRequestDetails,
    type CloverHeartbeatResponseDetails,
    CloverReadinessCheckStatus,
    CloverReadinessFailureReason,
    CloverReadinessUnverifiedReason,
    type CloverReadinessCheck,
    type CloverReadinessNotReadyCheck,
    type CloverReadinessReadyCheck,
    type CloverReadinessUnverifiedCheck,
    type CloverReadinessResult,
    type CloverSurchargeConfiguration,
    type CloverSurchargeReadiness,
    CloverOAuthAuthorizationState,
    type CloverOAuthAuthorizationStatus,
    CloverOAuthRefreshIntervalMinutes,
    CloverOAuthRefreshScheduleState,
    CloverPaymentsInfrastructureState,
    CloverPaymentsInfrastructureTerminalKind,
    type CloverPaymentsInfrastructureRequest,
    type CloverPaymentsInfrastructureTerminal,
    type CloverPaymentsInfrastructureStatus,
    type CloverPaymentsInfrastructureSynchronizationResult,
    type RenewCloverOAuthAuthorizationRequest,
    type RenewCloverOAuthAuthorizationResponse,
    type UpdateCloverOAuthRefreshIntervalRequest,
} from '../../../types/ipg/clover/clover-heartbeat.types';
import { MerchantTimeZoneSchema } from '../../../timezone.utils';
import { JsonLosslessNumberSchema } from '../../common.schemas';

export const CloverHeartbeatRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'GET'>;
    path: z.ZodLiteral<'/v3/merchants/{merchantId}'>;
    merchantId: z.ZodString;
}> = z.object({
    method: z.literal('GET'),
    path: z.literal('/v3/merchants/{merchantId}'),
    merchantId: z.string().min(1),
}) satisfies z.ZodType<CloverHeartbeatRequestDetails>;

export const CloverHeartbeatMerchantDetailsSchema: z.ZodObject<{
    id: z.ZodString;
    name: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1),
    name: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverHeartbeatMerchantDetails>;

export const CloverHeartbeatNormalizedResultSchema: z.ZodType<CloverHeartbeatNormalizedResult> =
    z.object({
        merchantId: z.string().min(1),
        status: z.enum([
            CloverHeartbeatOutcome.SUCCESS,
            CloverHeartbeatOutcome.UNAUTHORIZED,
            CloverHeartbeatOutcome.MALFORMED_RESPONSE,
        ]),
        message: z.string().min(1).optional(),
        merchantName: z.string().min(1).optional(),
    }) satisfies z.ZodType<CloverHeartbeatNormalizedResult>;

export const CloverHeartbeatSuccessResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverHeartbeatOutcome.SUCCESS>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CloverHeartbeatMerchantDetailsSchema;
}> = z.object({
    outcome: z.literal(CloverHeartbeatOutcome.SUCCESS),
    httpStatus: z.literal(200),
    body: CloverHeartbeatMerchantDetailsSchema,
});

export const CloverHeartbeatUnauthorizedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverHeartbeatOutcome.UNAUTHORIZED>;
    httpStatus: z.ZodUnion<[z.ZodLiteral<401>, z.ZodLiteral<403>]>;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverHeartbeatOutcome.UNAUTHORIZED),
    httpStatus: z.union([z.literal(401), z.literal(403)]),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CloverHeartbeatMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverHeartbeatOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverHeartbeatOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CloverHeartbeatResponseDetailsSchema: z.ZodType<CloverHeartbeatResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverHeartbeatSuccessResponseDetailsSchema,
        CloverHeartbeatUnauthorizedResponseDetailsSchema,
        CloverHeartbeatMalformedResponseDetailsSchema,
    ]);

export const CloverReadinessFailureReasonSchema: z.ZodType<CloverReadinessFailureReason> = z.enum([
    CloverReadinessFailureReason.UNAUTHORIZED,
    CloverReadinessFailureReason.UNAVAILABLE,
    CloverReadinessFailureReason.OFFLINE,
    CloverReadinessFailureReason.MALFORMED_RESPONSE,
    CloverReadinessFailureReason.TIMEOUT,
    CloverReadinessFailureReason.INACTIVE,
    CloverReadinessFailureReason.NOT_CONFIGURED,
    CloverReadinessFailureReason.UNSUPPORTED,
]);

export const CloverReadinessReadyCheckSchema: z.ZodType<CloverReadinessReadyCheck> = z
    .object({
        status: z.literal(CloverReadinessCheckStatus.READY),
    })
    .strict();

export const CloverReadinessNotReadyCheckSchema: z.ZodType<CloverReadinessNotReadyCheck> = z
    .object({
        status: z.literal(CloverReadinessCheckStatus.NOT_READY),
        reason: CloverReadinessFailureReasonSchema,
        message: z.string().trim().min(1),
    })
    .strict();

export const CloverReadinessUnverifiedReasonSchema: z.ZodType<CloverReadinessUnverifiedReason> =
    z.enum([CloverReadinessUnverifiedReason.SAFE_PROBE_UNAVAILABLE]);

export const CloverReadinessUnverifiedCheckSchema: z.ZodType<CloverReadinessUnverifiedCheck> = z
    .object({
        status: z.literal(CloverReadinessCheckStatus.UNVERIFIED),
        reason: CloverReadinessUnverifiedReasonSchema,
        message: z.string().trim().min(1),
    })
    .strict();

export const CloverReadinessCheckSchema: z.ZodType<CloverReadinessCheck> = z.union([
    CloverReadinessReadyCheckSchema,
    CloverReadinessNotReadyCheckSchema,
    CloverReadinessUnverifiedCheckSchema,
]);

export const CloverSurchargeConfigurationSchema: z.ZodType<CloverSurchargeConfiguration> =
    z.discriminatedUnion('supported', [
        z.object({ supported: z.literal(false) }).strict(),
        z
            .object({
                supported: z.literal(true),
                rate: JsonLosslessNumberSchema.refine(
                    (rate) => /^(?:0(?:\.\d+)?|1(?:\.0+)?)$/u.test(rate.value),
                    'Clover surcharge rate must be between zero and one'
                ),
            })
            .strict(),
    ]);

export const CloverSurchargeReadinessSchema: z.ZodType<CloverSurchargeReadiness> = z
    .object({
        check: CloverReadinessCheckSchema,
        configuration: CloverSurchargeConfigurationSchema.nullable(),
    })
    .strict()
    .superRefine((value, context) => {
        if (
            value.check.status === CloverReadinessCheckStatus.READY &&
            value.configuration === null
        ) {
            context.addIssue({
                code: 'custom',
                message: 'Ready Clover surcharge capability requires verified configuration',
                path: ['configuration'],
            });
        }
    });

export const CloverReadinessResultSchema: z.ZodType<CloverReadinessResult> = z.object({
    credential: CloverReadinessCheckSchema,
    ecommerceKey: CloverReadinessCheckSchema.optional(),
    cloudPayDevice: CloverReadinessCheckSchema.optional(),
    multiPay: CloverReadinessCheckSchema.optional(),
    surcharge: CloverSurchargeReadinessSchema.optional(),
});

export const CloverOAuthAuthorizationStatusSchema: z.ZodType<CloverOAuthAuthorizationStatus> = z
    .object({
        state: z.enum([
            CloverOAuthAuthorizationState.NOT_CONNECTED,
            CloverOAuthAuthorizationState.ACTIVE,
            CloverOAuthAuthorizationState.REAUTHORIZATION_REQUIRED,
            CloverOAuthAuthorizationState.DISCONNECTED,
        ]),
        access_token_expires_ts: z.string().datetime().nullable(),
        refresh_token_expires_ts: z.string().datetime().nullable(),
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
        refresh_interval_minutes: z
            .number()
            .int()
            .min(CloverOAuthRefreshIntervalMinutes.MINIMUM)
            .max(CloverOAuthRefreshIntervalMinutes.MAXIMUM),
        refresh_schedule: z.discriminatedUnion('state', [
            z
                .object({
                    state: z.literal(CloverOAuthRefreshScheduleState.SCHEDULED),
                    next_refresh_executes_ts: z.string().datetime(),
                    merchant_timezone: MerchantTimeZoneSchema.nullable(),
                })
                .strict(),
            z.object({ state: z.literal(CloverOAuthRefreshScheduleState.NOT_CONNECTED) }).strict(),
            z.object({ state: z.literal(CloverOAuthRefreshScheduleState.INACTIVE) }).strict(),
            z
                .object({
                    state: z.literal(CloverOAuthRefreshScheduleState.REAUTHORIZATION_REQUIRED),
                })
                .strict(),
            z
                .object({ state: z.literal(CloverOAuthRefreshScheduleState.SCHEDULING_PENDING) })
                .strict(),
        ]),
    })
    .strict();

export const RenewCloverOAuthAuthorizationRequestSchema: z.ZodType<RenewCloverOAuthAuthorizationRequest> =
    z.object({ organization_uuid: z.string().uuid() }).strict();

export const UpdateCloverOAuthRefreshIntervalRequestSchema: z.ZodType<UpdateCloverOAuthRefreshIntervalRequest> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            refresh_interval_minutes: z
                .number()
                .int()
                .min(CloverOAuthRefreshIntervalMinutes.MINIMUM)
                .max(CloverOAuthRefreshIntervalMinutes.MAXIMUM),
        })
        .strict();

export const RenewCloverOAuthAuthorizationResponseSchema: z.ZodType<RenewCloverOAuthAuthorizationResponse> =
    z.discriminatedUnion('renewal_outcome', [
        z.object({ renewal_outcome: z.literal('REFRESHED') }).strict(),
        z
            .object({
                renewal_outcome: z.literal('REAUTHORIZATION_REQUIRED'),
                authorization_url: z.string().url(),
                expires_ts: z.string().datetime(),
                allowed_websites: z.array(AllowedWebsiteSchemaIPG),
            })
            .strict(),
    ]);

export const CloverPaymentsInfrastructureRequestSchema: z.ZodType<CloverPaymentsInfrastructureRequest> =
    z.object({ organization_uuid: z.string().uuid() }).strict();

export const CloverPaymentsInfrastructureTerminalSchema: z.ZodType<CloverPaymentsInfrastructureTerminal> =
    z.discriminatedUnion('kind', [
        z
            .object({
                kind: z.literal(CloverPaymentsInfrastructureTerminalKind.VIRTUAL),
                terminal_device_uuid: z.string().uuid(),
                name: z.string().nullable(),
            })
            .strict(),
        z
            .object({
                kind: z.literal(CloverPaymentsInfrastructureTerminalKind.PHYSICAL),
                terminal_device_uuid: z.string().uuid(),
                name: z.string().nullable(),
                serial: z.string().min(1),
            })
            .strict(),
    ]);

export const CloverPaymentsInfrastructureStatusSchema: z.ZodType<CloverPaymentsInfrastructureStatus> =
    z
        .object({
            state: z.enum([
                CloverPaymentsInfrastructureState.NOT_CONNECTED,
                CloverPaymentsInfrastructureState.NOT_PROVISIONED,
                CloverPaymentsInfrastructureState.PROVISIONED,
                CloverPaymentsInfrastructureState.MERCHANT_ACCOUNT_CONFLICT,
            ]),
            clover_merchant_id: z.string().min(1).nullable(),
            clover_merchant_name: z.string().nullable(),
            organization_name: z.string(),
            merchant_name_mismatch: z.boolean(),
            principal_assigned: z.boolean(),
            virtual_terminal_count: z.number().int().nonnegative(),
            physical_terminal_count: z.number().int().nonnegative(),
            terminals: z.array(CloverPaymentsInfrastructureTerminalSchema),
            conflicting_merchant_accounts: z.array(
                z
                    .object({
                        merchant_account_uuid: z.string().uuid(),
                        name: z.string().nullable(),
                        processor_mid: z.string(),
                    })
                    .strict()
            ),
        })
        .strict();

export const CloverPaymentsInfrastructureSynchronizationResultSchema: z.ZodType<CloverPaymentsInfrastructureSynchronizationResult> =
    z
        .object({
            status: CloverPaymentsInfrastructureStatusSchema,
            created_virtual_terminal_count: z.number().int().nonnegative(),
            created_physical_terminal_count: z.number().int().nonnegative(),
            restored_virtual_terminal_count: z.number().int().nonnegative(),
            restored_physical_terminal_count: z.number().int().nonnegative(),
            removed_virtual_terminal_count: z.number().int().nonnegative(),
            removed_physical_terminal_count: z.number().int().nonnegative(),
            failed_physical_device_serials: z.array(z.string().min(1)),
        })
        .strict();
